// Profit On Your Terms's Start here: the words the screens draw themselves, ported from the live
// course site's own components (course.adamslee.co.uk, its one index.html: the step renderer, the
// worked examples, the comparables filter and street sorter, the quick checks, the calculator, the
// 3-house drill, the 7 channels, the channel choice, the letter budget, the assignments and the
// gate), word for word, except where Adam's writing rules change them. Each change is marked
// "Rules:" below. The screens' own words (titles, bodies, cards, feedback, takeaways, the quick
// checks) come from the database (hub.steps, seed 86_poyt.sql), not from here.
//
// No board draws the screens (POYT.dc.html draws only the 2 rows that open them), so every line here
// is on the list for Adam's walkthrough. The money figures are the course's own teaching figures, as
// on live: the calculator's starting numbers and the letter budget's band.

export const COPY = {
  // The rail and the bar above a screen (live's sidebar)
  railTitle: 'Start here',
  screensDone: (n, total) => `${n} of ${total} screens done`,
  // the screen's eyebrow: the row's label and title, "Section 1, Deal analysis"
  eyebrow: (label, title) => (label ? `${label}, ${title}` : title),
  allScreens: 'All screens',
  hideScreens: 'Hide the screens',
  lockedWord: 'Locked',
  doneWord: 'Done',

  // A screen (live's Step)
  // Rules: live has "In one line"
  takeaway: 'In 1 line',
  back: 'Back',
  doneTag: 'Done',
  nextScreen: 'Next screen',
  doneNextScreen: 'Done, next screen',
  finish: 'Finish',
  // live's gate help, beside the button while something on the screen is still to answer
  gateHelp: {
    cards: 'Answer the questions above to continue',
    quiz: 'Answer the question above to continue',
    walk: 'Step through the worked example to continue',
    calc: 'Try the calculator above to continue',
    drill: n => `Get all ${n} houses right to continue`,
    channels: n => `Open all ${n} channels to continue`,
    choice: 'Record your second channel to continue',
    budget: 'Save your plan to continue',
    assignment: 'Tick off the assignment above to continue',
  },

  // A screen not open yet (the database's screen_locked line), and the way back to where they're up to
  locked: 'Do the screens before this one first.',

  // What the database refuses (docs/data-model.md 10.6.2), and a save that didn't reach it
  refused: {
    screen_locked: 'Do the screens before this one first.',
    cards: 'Make a call on every card first.',
    walk: 'Step through the example to the end first.',
    calc: 'Try the calculator first.',
    drill: 'Get all 3 houses right first.',
    channels: 'Open all 7 first.',
    choice: 'Pick your second channel first.',
    budget: 'Save your plan first.',
    gate: 'Tick all 3 first.',
    quiz: 'Answer the quick check first.',
    assignment: 'Tick them all first.',
    step_not_open: "This isn't open to you.",
    fallback: 'Something went wrong. Try again.',
  },
  saveFailed: "That didn't save. Check your connection and try again.",

  // The formula (live's Formula)
  formulaLead: 'Cash offer = ',
  formulaPct: '75%',
  formulaRest: ' of value, minus the refurb, minus every buying cost',
  formulaNote: 'Value is GDV where work is needed, as-is true market value where not. Legals, survey, SDLT and finance all count. The number never moves.',

  // The script blocks in the words (">>" lines)
  sayThis: 'Say this',

  // The worked examples (live's Walkthrough)
  nextStepOfDeal: 'Next step of the deal',
  // Rules: live shows an em dash for a figure not reached yet; here it's an empty mark, read as this
  figureNotYet: 'Not yet',

  // The comparables filter and the street sorter (live's CardJudge)
  keepIt: 'Keep it',
  leaveItOut: 'Leave it out',
  keep: 'Keep',
  amber: 'Amber',
  avoid: 'Avoid',
  right: 'Right.',
  notThisOne: 'Not this one.',
  // Rules: live has "· N to go"; the hub's lines use a comma
  tally: (right, tried, left) => `${right} of ${tried} called right so far, ${left} to go`,

  // The quick check (live's Quiz)
  quickCheck: 'Quick check',
  notQuite: 'Not quite.',

  // The cash offer calculator (live's Calculator)
  needsWork: 'Does the house need work?',
  yesRefurb: 'Yes, refurb needed',
  noWork: 'No work needed',
  gdv: 'GDV',
  gdvSub: 'Finished value, supported by done-up comparables',
  asIs: 'As-is true market value',
  asIsSub: "What it's worth today, from your comparables",
  refurb: 'Refurb estimate',
  refurbSub: 'Cautious to the point of embarrassment, contingency in',
  costs: 'All buying costs',
  costsSub: 'Legals, survey, SDLT, any finance costs',
  yourCashOffer: 'Your cash offer',
  working: (value, line, refurb, costs) => `75% of ${value} is ${line}, minus ${refurb} refurb, minus ${costs} costs. All money out if accepted. It never moves.`,

  // The 3-house drill (live's Drill)
  // Rules: live has "House 1 · The tired terrace"
  house: (n, name) => `House ${n}, ${name}`,
  check: 'Check',
  notYet: 'Not yet.',
  youEntered: (amount, hint) => `You entered ${amount}. Run it again: ${hint}`,
  houseAnswerLabel: n => `Your cash offer for house ${n}`,

  // The 7 channels (live's ChannelMatrix and Channels)
  // Rules: live has "The seven at a glance" and "the ones you are choosing between"
  sevenTitle: n => `The ${n} at a glance`,
  sevenSub: "Compare first, then open the ones you're choosing between.",
  colChannel: 'Channel',
  colCost: 'Cost',
  colSpeed: 'Speed to first lead',
  colEffort: 'Effort',
  costLabel: 'Cost:',
  speedLabel: 'Speed:',
  effortLabel: 'Effort:',

  // The campaign rule (live's ChannelChoice)
  // Rules: live has "Channel 1 · Direct-to-vendor letters"
  channelOne: 'Channel 1, Direct-to-vendor letters',
  channelOneNote: 'Mandatory. Already yours. Not up for debate.',
  chooseSecond: 'Choose your second channel',
  recorded: 'Recorded.',
  // Rules: live has "the six weeks", and lowercases the whole label ("facebook ads"); here a name
  // keeps its capital
  recordedLine: label => `Letters plus ${lowerFirst(label)}. That's your campaign for the 6 weeks. Everything else stays switched off.`,
  agentsNote: "Agents aren't on this list on purpose. They get a full session in Week 5, and until then nothing is required of you there.",

  // The letter budget (live's BudgetMapper). Rules: live has "the six weeks" twice.
  batch: 'First batch size',
  batchSub: '100 letters or more. Below 100 the maths of response rates stops working for you.',
  sends: 'Sends across the 6 weeks',
  sendsSub: 'First batch plus roughly monthly repeats to the same list',
  cpl: 'Cost per letter (£)',
  cplSub: "Printed at home and posted second class you'll land around 85p to £1 including the data. Managed sending costs more; it buys time, not response.",
  second: 'Second channel',
  secondFromChoice: "Pulled from your recorded choice; change it there if it's wrong.",
  secondNoChoice: "You haven't recorded a choice yet; pick one on the campaign rule screen.",
  secondSpend: 'Second channel spend (£)',
  lettersLine: (batch, sends, cpl) => `Letters (${batch} × ${sends} sends × £${cpl})`,
  secondLine: 'Second channel',
  totalLine: 'Campaign total, 6 weeks',
  band: ['£0', '£100', '£200', '£300+'],
  verdictLettersOver: 'Your letters alone are past £200. Trim the cost per letter first: print at home, post second class. Volume is the last lever, and it never drops below 100 a batch.',
  verdictInside: 'Inside the £100 to £200 band, letters protected. Spend it and stop tinkering.',
  verdictAds: "Ads push you past the band. That's a deliberate purchase of speed, and it's fine if the money's real. What never happens: cutting letters to pay for ads.",
  verdictOver: 'Past the band. Letters come first; look at the secondary spend before you touch the batch.',
  savePlan: 'Save my plan',
  savedPlan: amount => `Saved: ${amount} planned`,

  // The assignments (live's Assignment)
  yourAssignment: 'Your assignment',
  ticksSave: "Ticks save as you go. Adam sees them, so tick what's actually done.",

  // The gate (live's Gate). Rules: live has "All three true" and "session one".
  yourSecond: label => `Your recorded second channel: ${label}`,
  gateOpen: 'Gate open.',
  gateOpenLine: "All 3 true. You're arriving at session 1 with marketing running, which is exactly where this course wants you. See you on the first Wednesday.",
  postTheLetters: 'Post the letters.',

  // The diagrams (live's Visual*)
  // Rules: live has a curly apostrophe ("LENDER’S"); the minus sign before an amount is a plain hyphen
  lendersLine: pct => `LENDER'S ${pct}% LINE`,
  finishedValue: 'Finished value',
  gapBar: 'The 25% gap',
  refurbBar: 'Refurb',
  costsBar: 'Buying costs',
  offerBar: 'Your cash offer',
  theyThink: "They think it's worth",
  youSay: "You're going to say",
  gapOutLoud: 'The gap, out loud',
  waterfallLabel: 'The cash offer built backwards from the finished value',
  colComparable: 'Comparable',
  colType: 'Type',
  colSold: 'Sold',
  colPrice: 'Price',
  colCondition: 'Condition',
  ceiling: 'Your ceiling',
  floor: 'Your floor',
  excluded: 'Excluded',
  asIsValue: 'As-is value',
  comparablesUsed: 'Comparables used',
  let: 'let',
  stillAsking: 'still asking',
  // Rules: live has "N days on the market · note"
  daysOnMarket: (days, note) => `${days} days on the market, ${note}`,
  inYourStack: 'What goes in your stack',
  offerOne: 'Offer 1',
  cashOffer: 'The cash offer',
  buildToday: 'You can build this today',
  offerTwo: 'Offer 2',
  creativeOffer: 'The creative offer',
  buildWeeks: 'You build this in weeks 1 to 6',
  funnelLabel: 'Narrowing from a town to named streets',
  nodeLabel: 'The sweet spot sits 2 roads back from the node',
  node: 'NODE',
  sweetSpot: 'the sweet spot',
}

// "Facebook ads" stays "Facebook ads"; "Leaflets" reads "leaflets" after "Letters plus"
const NAMES = ['Facebook', 'Google', 'Land Registry', 'Rightmove', 'OpenRent', 'Property Data']
export function lowerFirst(label) {
  const s = String(label || '')
  if (!s || NAMES.some(n => s.startsWith(n))) return s
  return s.charAt(0).toLowerCase() + s.slice(1)
}

// Live's money: "£" and the whole pounds, with commas
export function gbp(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '£0'
  return `£${Math.round(Number(n)).toLocaleString('en-GB')}`
}
